import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Container, Link, Paper, Stack, Typography } from '@mui/material';
import { accountUpdateError, confirmEmailChange } from '../../gateway/accounts';
import { ROUTE_LOGIN } from '../../UkloRoutes';
import { useAccount } from './AccountContext';

type Outcome = 'pending' | 'confirmed' | 'taken' | 'invalid';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { invalidatePromise, isLoggedIn } = useAccount();
  const [outcome, setOutcome] = useState<Outcome>('pending');
  const requested = useRef(false);

  useEffect(() => {
    // Tokens are single use, so make sure we only submit once
    if (requested.current) {
      return;
    }
    requested.current = true;

    confirmEmailChange(token)
      .then(() => setOutcome('confirmed'))
      .then(() => invalidatePromise())
      .catch(error => setOutcome(accountUpdateError(error)?.reason === 'email' ? 'taken' : 'invalid'));
  }, [token]);

  return (
    <Container component="main" maxWidth="xs">
      <Paper sx={{ mt: 4, p: 4, pb: 3 }}>
        <Stack alignItems="center" spacing={3}>
          <Typography component="h1" variant="h5">
            Confirm your new email address
          </Typography>
          {outcome === 'pending' && <Typography>Confirming...</Typography>}
          {outcome === 'confirmed' && (
            <Alert severity="success">
              Your email address has been changed. Please use it to log in from now on.
            </Alert>
          )}
          {outcome === 'taken' && (
            <Alert severity="error">
              This email address is already used by another account, so we couldn't change your email address.
            </Alert>
          )}
          {outcome === 'invalid' && (
            <Alert severity="error">
              Unable to confirm your new email address. Your link may have expired, or you may have requested a
              different address since. Please request the change again from your account settings.
            </Alert>
          )}
          {!isLoggedIn && (
            <Link component={RouterLink} to={ROUTE_LOGIN} variant="body2">
              Go to login
            </Link>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
